import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { trackEvent } from '@/utils/analytics';
import { isSpotlightHotkey, isTypingInForeignDialog } from './searchSpotlightHelpers';

type SearchSpotlightContextValue = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
};

const SearchSpotlightContext = createContext<SearchSpotlightContextValue | null>(null);

export function SearchSpotlightProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen(prev => !prev), []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isSpotlightHotkey(event)) return;
      if (isTypingInForeignDialog(event)) return;
      event.preventDefault();
      if (!isOpenRef.current) {
        trackEvent({
          name: 'search_spotlight_open',
          buttonId: 'search_spotlight_hotkey',
          label: 'Open search spotlight',
          page: 'global',
          section: 'keyboard',
          feature: 'search_spotlight',
          eventCategory: 'search',
        });
      }
      toggle();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [toggle]);

  const value = useMemo(
    () => ({
      isOpen,
      open,
      close,
      toggle,
    }),
    [isOpen, open, close, toggle],
  );

  return <SearchSpotlightContext.Provider value={value}>{children}</SearchSpotlightContext.Provider>;
}

export function useSearchSpotlight(): SearchSpotlightContextValue {
  const context = useContext(SearchSpotlightContext);
  if (!context) {
    throw new Error('useSearchSpotlight must be used within SearchSpotlightProvider');
  }
  return context;
}

export function useSearchSpotlightOptional(): SearchSpotlightContextValue | null {
  return useContext(SearchSpotlightContext);
}
